/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks";
import {useFetchToken} from "../../../../../../api/helper";
import {
  fetchMergedDataSetsForAdasProgram,
  selectEditSensorWizard,
  selectCurrentStudyId,
  selectStudy,
  setEditSensorWizardSelectedDataSet
} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign";
import {selectConfigSensorWizardSelectSensorStep, selectSensorBomData} from "../../../../../../redux/ducks/AdasPrograms/slice";
import React, {useEffect, useState} from "react";
import {selectProgram} from "../../../../../../redux/ducks/AdasPrograms";
import ADAS_PROGRAMS_CONSTANTS from "../../../../AdasPrograms/constants";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import ArgosCheckbox from "../../../../../components/Form/Checkbox/ArgosCheckbox";
import {TreeItem} from "@mui/x-tree-view/TreeItem";
import Loader from "../../../../../components/Loader/Loader";
import Typography from "@mui/material/Typography";
import {SimpleTreeView} from "@mui/x-tree-view/SimpleTreeView";
import {ArgosCollapseNodeIcon, ArgosExpandNodeIcon} from "../../../../../components/Icons/ArgosTreeIcons";

export const SelectDataSetStep = () => {

  const dispatch = useAppDispatch()

  const fetchToken = useFetchToken()

  const formData = useAppSelector(selectEditSensorWizard).data
  const isFetching = formData?.dataSets?.isFetching ?? false
  const dataSetsData = formData?.dataSets?.data ?? {}
  const selectedDataSetIds: string[] = formData?.dataSets?.selectedDataSet ?? []

  const selectSensorStepState = useAppSelector(selectConfigSensorWizardSelectSensorStep)

  /**
   * The previous dataset ID comes from the saved experiment data (it is what was selected and saved before).
   */
  const previousDatasetIds = useAppSelector(state => {
    const sensorId = selectSensorStepState?.id
    let locationKey = selectSensorStepState?.locationKey

    if (typeof locationKey === 'string') {
      locationKey = [locationKey]
    }

    if (sensorId && locationKey) {
      const sensorData = selectSensorBomData(state)?.data?.sensors?.[sensorId]?.sensorLocations?.filter((loc: any) =>
        loc.locationKey === locationKey?.[0]
      )[0]
      let datasetIds = sensorData?.redlineDatasetId
      if (typeof datasetIds === 'string' && datasetIds.length > 0) {
        datasetIds = [datasetIds]
      }
      if (Array.isArray(datasetIds) && datasetIds.length > 0) {
        return datasetIds
      }
    }
    return undefined
  })

  /**
   * Set formData selectedDataSet to previous datasetId if it exists
   */
  useEffect(() => {
    if (selectedDataSetIds.length === 0 && previousDatasetIds) {
      dispatch(setEditSensorWizardSelectedDataSet(previousDatasetIds))
    }
  }, [selectedDataSetIds, previousDatasetIds]);

  /**
   * Get adas program id
   */
  const currentStudyId = useAppSelector(selectCurrentStudyId);
  const study = useAppSelector((state) => selectStudy(state, currentStudyId ?? ""));
  const adasProgram = useAppSelector((state) => selectProgram(state, study?.adasProgramId ?? ""));
  const adasProgramId = adasProgram?.id;
  const adasProgramName = adasProgram ? ADAS_PROGRAMS_CONSTANTS.helperFunctions.getFullAdasProgramNameWithNameAndModelYear(adasProgram.data.programName, adasProgram.data.modelYear) : adasProgramId


  /**
   * Fetches merged datasets for the given ADAS program ID.
   *
   * @param {string} adasProgramId - The ID of the ADAS program.
   * @param {string} studyId - The ID of the current study.
   * @returns {Promise<void>} A promise that resolves when the datasets are fetched.
   */
  const fetchMergedDataSetsForAdasProgramData = async (adasProgramId: string, studyId: string) => {
    const tokenFunction = await fetchToken;
    const idToken = await tokenFunction();
    dispatch(fetchMergedDataSetsForAdasProgram({idToken, adasProgramId, greenLineInfo: {studyId}}));
  };

  /**
   * Effect to fetch merged datasets for the ADAS program when the ADAS program ID changes.
   */
  useEffect(() => {
    if (!adasProgramId || !currentStudyId) return;
    fetchMergedDataSetsForAdasProgramData(adasProgramId, currentStudyId);
  }, [adasProgramId, currentStudyId]);

  /**
   * Renders a tree structure recursively.
   *
   * @param {Object} nodes - The current node being processed.
   * @returns {React.ReactNode|null} The rendered tree item or null if no nodes are provided.
   */
  const renderTree = (nodes: any) => {
    if (!nodes) return null

    const treeItemLabel = nodes.name
    const nodeType = nodes.nodeType

    let labelContent: React.ReactNode
    if (nodeType !== 'dataset' || nodes?.children?.length) {
      /** Includes study, merge set, folder */
      labelContent = (
        <div style={{display: 'flex', height: 42, alignItems: 'center'}}>
          {treeItemLabel}
        </div>
      )
    } else {
      /** Includes merged data sets only */
      labelContent = (
        <ArgosFlexbox style={{alignItems: 'center'}}>
          <ArgosCheckbox
            onChange={(event) => {
                if (event.target.checked) {
                  dispatch(setEditSensorWizardSelectedDataSet([...selectedDataSetIds, nodes.datasetId]))
                } else {
                  dispatch(setEditSensorWizardSelectedDataSet(selectedDataSetIds.filter(id => id !== nodes.datasetId)))
                }
            }}
            onClick={(event) => {
              event.stopPropagation()
            }}
            checked={selectedDataSetIds.includes(nodes.datasetId)}
          />
          <span>
            {treeItemLabel}
          </span>
        </ArgosFlexbox>
      )
    }

    const nodeKey = nodes.studyId ? (`${nodes.nodeId}-${nodes.studyId}`) : nodes.nodeId
    return (
      <TreeItem
        key={nodeKey}
        itemId={nodeKey}
        label={labelContent}
      >
        {Array.isArray(nodes.children)
          ? nodes.children.map((node: any) => renderTree(node))
          : null}
      </TreeItem>
    )
  }


  /**
   * State to keep track of expanded node IDs.
   */
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([])

  /**
   * Recursively finds and collects the IDs of nodes that should be expanded (which are any nodes that are parents of the selected dataset).
   *
   * @param {Object} nodes - The current node being processed.
   * @param {string[]} _selectedDataSetIds - The IDs of the selected datasets.
   * @param {string[]} [expandedNodes=[]] - The array to collect expanded node IDs.
   * @param {string[]} [parentNodes=[]] - The array to keep track of parent node IDs.
   * @returns {string[]} The array of expanded node IDs.
   */
  const findExpandedNodes = (nodes: any, _selectedDataSetIds: string[], expandedNodes: string[] = [], parentNodes: string[] = []) => {
    if (!nodes) return expandedNodes;

    // Generate a unique key for the current node
    const nodeKey = nodes.studyId ? (`${nodes.nodeId}-${nodes.studyId}`) : nodes.nodeId;

    // If the current node matches the selected dataset ID, add it and its parents to expandedNodes
    if (_selectedDataSetIds.includes(nodes.datasetId)) {
      expandedNodes.push(...parentNodes, nodeKey);
    }

    // Recursively process child nodes
    if (nodes.children) {
      nodes.children.forEach((child: any) => {
        findExpandedNodes(child, _selectedDataSetIds, expandedNodes, [...parentNodes, nodeKey]);
      });
    }

    // Ensure the root node is always included
    if (nodeKey === 'fe-root') {
      expandedNodes.push(nodeKey);
    }

    return expandedNodes;
  };

  /**
   * Effect to update the expanded node IDs to auto-expand to the selected dataset.
   */
  useEffect(() => {
    if (selectedDataSetIds.length === 0 || Object.keys(dataSetsData).length === 0) {
      return
    }
    // Create a tree structure with a single root node
    const studyTree = {nodeId: 'fe-root', children: Object.values(dataSetsData)};

    // Find the nodes that should be expanded
    const expandedNodes = findExpandedNodes(studyTree, selectedDataSetIds);

    // Update the state with the expanded node IDs
    setExpandedNodeIds(expandedNodes);
  }, [dataSetsData, selectedDataSetIds]);

  const treeContent = renderTree({nodeId: 'fe-root', children: Object.values(dataSetsData)});

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%', gap: 20, padding: '10px'}}>
      <Loader isLoading={isFetching}>
        {Object.keys(dataSetsData).length === 0 ?
          <ArgosFlexbox vertical style={{height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center'}}>
            <Typography style={{textAlign: 'center'}}>
              There are no merged data sets with {adasProgramName} package
            </Typography>
            <Typography style={{textAlign: 'center'}}>
              Create merged data sets in Coverage Analysis or choose a different ADAS Program
            </Typography>
          </ArgosFlexbox>
          : <SimpleTreeView
            slots={{
              collapseIcon: ArgosExpandNodeIcon,
              expandIcon: ArgosCollapseNodeIcon,
            }}
            sx={{flexGrow: 1, overflowY: 'auto'}}
            expandedItems={expandedNodeIds}
            onExpandedItemsChange={(event: any, nodeIds: React.SetStateAction<string[]>) => {
              setExpandedNodeIds(nodeIds)
            }}
          >
            {treeContent}
          </SimpleTreeView>}
      </Loader>
    </ArgosFlexbox>
  )
}